import { ISportStatisticsSubscriptionResponse } from "@gp/hub";
import { observable, action, computed } from "mobx";

const defaultConfig = {
	periodDuration: null,
	periods: null,
	extraTime: false,
	overtimePeriodDuration: null,
	overtimePeriods: null,
	valueYMax: 100,
	valueKey: "value",
	progressKey: "minute",
	startingKeys: {
		minute: 1,
	},
};

const soccer = {
	periodDuration: 45,
	periods: 2,
	extraTime: true,
	overtimePeriodDuration: 15,
	overtimePeriods: 2,
};

function getSportConfig(sportAbrv: string) {
	switch (sportAbrv) {
		case "soccer":
			return { ...defaultConfig, ...soccer };
		default:
			return { ...defaultConfig };
	}
}

class PressureIndexStore {
	@observable values: { minute: number; value: number }[] = [];

	periodsPlaces: { type: string; value: number }[];

	regularTimeChartSize: number;
	overTimeChartSize: number;
	overtimePeriod: number;
	overtimePeriods: number;

	chartRef: { reflow: Function } | null = null;

	periodDuration: number;
	extraTime: boolean;
	overtimePeriodDuration: number;
	startingKeys: {
		minute: number;
	};
	valueYMax: number;
	valueKey: string;
	progressKey: keyof (typeof this.values)[number];
	periods: number;

	@computed get isInOverTime() {
		return (
			this.values.length > 0 &&
			this.values[this.values.length - 1][this.progressKey] >=
				this.periodsPlaces.filter((p) => p.type === "regular").length *
					this.periodDuration +
					1
		);
	}

	@computed get currentPeriods() {
		const allPeriods = this.isInOverTime
			? this.periodsPlaces
			: this.periodsPlaces.filter((p) => p.type === "regular");

		return allPeriods.map((period, idx) => {
			// @ts-expect-error postoji star nam je compiler
			const place = this.values.findLastIndex(
				(v: (typeof this.values)[number]) =>
					Math.floor(v[this.progressKey]) == period.value
			);

			if (place == -1) {
				const extraTimesBefore = this.extraMinutes.filter(
					(ex) => ex[this.progressKey] < period.value
				).length;

				return {
					...period,
					value: period.value + extraTimesBefore + idx,
				};
			}

			return {
				...period,
				value: place + 1 + idx,
			};
		});
	}

	@computed get currentChartTime() {
		if (this.values.length === 0) {
			return null;
		}

		const minuteHasNoValue =
			this.showValues[this.showValues.length - 1] == 0; // period hit or to show that period has started

		if (/*periodHit ||*/ minuteHasNoValue && this.values.length != 1) {
			return this.showValues.length - 1;
		}

		return this.showValues.length;
	}

	@computed get extraMinutes() {
		return this.values.filter(
			(x) => !Number.isInteger(x[this.progressKey])
		);
	}

	@computed get xAxisValue() {
		const time =
			this.regularTimeChartSize +
			this.extraMinutes.length +
			this.currentPeriods.length;

		if (this.isInOverTime) {
			return time + this.overtimePeriod;
		}

		return time;
	}

	@computed get showValues() {
		const vals = [...this.values];

		this.currentPeriods.forEach((per) => {
			if (per.value > vals.length) {
				return;
			}
			const periodObject = {
				[this.progressKey]: per.type,
				[this.valueKey]: 0,
			};

			vals.splice(
				per.value,
				0,
				periodObject as (typeof this.values)[number]
			);
		});
		return vals.map(
			(v) => v[this.valueKey as keyof (typeof this.values)[number]] || 0
		);
	}

	@computed get isViewAvailable() {
		return this.values.length > 0;
	}

	constructor() {}

	init(sportAbrv: string) {
		const sportConfig = getSportConfig(sportAbrv);

		Object.assign(this, sportConfig);

		this.calcPeriodDurations();
		this.setPeriodPlaces();
	}

	processMessage(
		value: ISportStatisticsSubscriptionResponse["data"]["data"]["pressureIndex"]
	) {
		if (!Array.isArray(value)) {
			this.setValues([]);
			return;
		}

		if (value.length == 0) {
			return;
		}

		const isFromBegining = Object.keys(this.startingKeys).every((key) => {
			return value[0][key] === this.startingKeys[key];
		});

		if (!isFromBegining && this.values.length > 0) {
			if (
				this.values[this.values.length - 1][this.progressKey] <
				value[0][this.progressKey]
			) {
				this.setValues([...this.values, ...value]);
				return;
			}

			const startIndex = this.values.findIndex(
				(v) => v[this.progressKey] == value[0][this.progressKey]
			);
			this.setValues([...this.values.slice(0, startIndex), ...value]);
			return;
		}

		this.setValues(value);
	}

	@action.bound
	setValues(values: typeof this.values) {
		this.values = values;
	}

	calcPeriodDurations = () => {
		this.regularTimeChartSize = this.periods * this.periodDuration;
		this.overtimePeriod =
			this.overtimePeriods * this.overtimePeriodDuration;
	};

	@action.bound
	setPeriodPlaces() {
		const periodPlaces = [];

		for (let i = 1; i <= this.periods; i++) {
			periodPlaces.push({
				type: "regular",
				value: i * this.periodDuration,
			});
		}

		for (let i = 1; i <= this.overtimePeriods; i++) {
			periodPlaces.push({
				type: "OT",
				value:
					this.regularTimeChartSize + i * this.overtimePeriodDuration,
			});
		}

		this.periodsPlaces = periodPlaces;
	}

	getLastMinuteReceived = () => {
		if (!this.values || this.values.length == 0) {
			return 0;
		}

		const lastMinute =
			this.values[this.values.length - 1][this.progressKey];

		if (Number.isInteger(lastMinute)) {
			return lastMinute;
		}

		return this.values[this.values.length - 2][this.progressKey] || 0;
	};

	@action.bound
	onDispose() {
		this.chartRef = null;
		this.values = [];
	}
}

export default PressureIndexStore;
